import { useApp, useBehavior, useTriggerAction, useUpdateWidgetValue } from '@libs/core';
import { useConsumerInput } from '../../hooks/useInputs';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import { CoreInputView, CoreLayoutView, CoreTextView, ORIENTATION_ENUM, TCoreLayoutStyleProps } from '@core/widgets';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { TInputPasscodeData } from '../../types/data';
import { TPasscodeOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import { authenticateAndDecryptPasscode, isPasscodeStored, registerAndEncryptPasscode } from './webauthn';
import styles from './inputPasscode.module.css';

const passcodeDotFilledStyleProps: TCoreLayoutStyleProps = {
    width: 20,
    height: 20,
    borderRadius: 9999,
    backgroundColor: 'var(--color-purple_100)',
    flexShrink: 0,
};

const passcodeDotUnfilledStyleProps: TCoreLayoutStyleProps = {
    ...passcodeDotFilledStyleProps,
    backgroundColor: 'var(--color-grey_8_lc)',
};

const InputPasscode = ({ widget }: TWidgetProps<TInputPasscodeData, TPasscodeOption>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const { value, errorMessage } = useConsumerInput(widget, { isHidden, isDisabled, triggerActionsByType, setModal });
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const passcodeRef = useRef<HTMLInputElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const biometryCancelledRef = useRef<boolean>(false);

    const options = widget.options ?? {};
    const useBiometry = options.useBiometry ?? false;
    const isSetPasscode = options.isSetPasscode ?? false;
    const maxLength = options.maxLength ?? 4;

    const [touched, setTouched] = useState(false);
    const [prevIsError, setPrevIsError] = useState(!!options.isError);

    if (!!options.isError !== prevIsError) {
        setPrevIsError(!!options.isError);
        if (options.isError) {
            setTouched(false);
        }
    }

    const isError = !!options.isError && !touched;

    const updateValueForPassCode = (value: string, allowTriggerAction?: boolean) => {
        updateWidgetValue(value);
        if (allowTriggerAction && value?.length === maxLength) {
            triggerActionsByType(ActionActionTypeEnum.onFilled)
                .then()
                .catch((e) => console.error(e));
        }
    };

    const triggerCheckWebAuth = () => {
        authenticateAndDecryptPasscode()
            .then((passcode) => updateValueForPassCode(passcode ?? '', true))
            .catch((e) => {
                if (e instanceof Error && e.name === 'NotAllowedError') {
                    biometryCancelledRef.current = true;
                } else {
                    console.error(e);
                }
            });
    };

    const checkPassCodeInWebAuth = useEffectEvent(() => {
        if (useBiometry && isPasscodeStored()) {
            triggerCheckWebAuth();
        }
    });

    useEffect(() => {
        if (options.isError) {
            updateWidgetValue('');
        } else {
            checkPassCodeInWebAuth();
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [updateWidgetValue, options.isError]);

    const focusInput = () => {
        passcodeRef.current?.focus();
    };

    useEffect(() => {
        const focusTimer = setTimeout(() => passcodeRef.current?.focus(), 100);
        return () => clearTimeout(focusTimer);
    }, [options.isError]);

    const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
        if (value?.length < maxLength) {
            timerRef.current = setTimeout(() => {
                passcodeRef.current?.focus();
            }, 100); // Small delay helps in Safari
        }
    };

    const handleChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!touched) {
            setTouched(true);
        }
        const value = e?.target?.value;

        updateValueForPassCode(value);
        if (value?.length === maxLength) {
            if (isSetPasscode && !biometryCancelledRef.current) {
                registerAndEncryptPasscode(value)
                    .catch((e) => console.error(e))
                    .finally(() => {
                        updateValueForPassCode(value, true);
                    });
            } else {
                updateValueForPassCode(value, true);
            }
        }
    };

    const currentLength = value?.length ?? 0;
    const parentStyleNames = [options.style].filter(Boolean) as string[];
    const showBiometryButton = useBiometry && isPasscodeStored() && !!widget.data?.messages?.useBothBiometryTitle;

    return (
        <CoreLayoutView
            uid={widget.uid}
            isHide={isHidden}
            styleName="passcode"
            parentStyleNames={parentStyleNames}
            styleProps={{
                orientation: ORIENTATION_ENUM.VERTICAL,
                lineX: 'center',
                lineY: 'center',
                backgroundColor: 'var(--color-white)',
                width: '100%',
                height: '100%',
                margin: options.margin,
            }}
            style={{ position: 'relative' }}
            onClick={focusInput}
        >
            {/* Hidden keyboard-capture input */}
            <CoreInputView
                uid={widget.uid + '_input'}
                dataTest={widget.uid + '_input'}
                className={styles.passCodeInput}
                inputRef={passcodeRef}
                value={value || ''}
                onBlur={handleBlur}
                onChange={handleChangeLocal}
                maxLength={maxLength}
                autoComplete="off"
                type="tel"
                inputMode="numeric"
                tabIndex={widget.data?.tabIndex}
                renderWrapper={(input) => <div className={styles.passCodeWrapper}>{input}</div>}
            />

            {/* Dot indicators row */}
            <CoreLayoutView
                uid={widget.uid + '_dots'}
                styleName="passcodeDots"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    orientation: ORIENTATION_ENUM.HORIZONTAL,
                    lineX: 'center',
                    gap: 24,
                    margin: { bottom: 24 },
                }}
            >
                {[...Array(maxLength)].map((_v: undefined, index: number) => (
                    <CoreLayoutView
                        key={index}
                        styleName={currentLength >= index + 1 ? 'passcodeDotFilled' : 'passcodeDotUnfilled'}
                        parentStyleNames={parentStyleNames}
                        styleProps={currentLength >= index + 1 ? passcodeDotFilledStyleProps : passcodeDotUnfilledStyleProps}
                        style={{
                            transitionDuration: '300ms',
                            transitionProperty: 'all',
                            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    />
                ))}
            </CoreLayoutView>

            {/* Error text */}
            {isError && !!errorMessage && (
                <CoreTextView
                    uid={widget.uid + '_errorText'}
                    text={errorMessage}
                    as="span"
                    styleName="passcodeError"
                    parentStyleNames={parentStyleNames}
                    styleProps={{ ...TextStyle.main, main: 'var(--color-pink_100)', margin: { bottom: 16, left: 16, right: 16 } }}
                />
            )}

            {/* Biometry button — only after a failed biometry attempt when useBothBiometryTitle is set */}
            {showBiometryButton && (
                <ButtonRowView
                    uid={widget.uid + '_useBiometry'}
                    iconName={'ic_face_id'}
                    label={widget.data?.messages?.useBothBiometryTitle}
                    alignment={'center'}
                    hierarchy={'primary'}
                    parentStyleNames={[...parentStyleNames, 'passcodeBiometryButton']}
                    onClick={() => {
                        triggerCheckWebAuth();
                    }}
                />
            )}
        </CoreLayoutView>
    );
};

export { InputPasscode };
